import React, { useCallback, useEffect, useMemo, useState } from "react";

// MyStockPicksAI v19.11.0 Swing Options Bot panel.
// Drop-in component. It intentionally defaults to disabled + paper mode.
export default function SwingBotPanel({ apiBase, token }) {
  const base = useMemo(() => (
    apiBase || process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || "https://mystockpicks.onrender.com"
  ).replace(/\/$/, ""), [apiBase]);
  const authToken = token || localStorage.getItem("token") || localStorage.getItem("jwt") || "";
  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
  }), [authToken]);

  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [scanRows, setScanRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const call = useCallback(async (path, opts = {}) => {
    const r = await fetch(`${base}${path}`, { ...opts, headers: { ...headers, ...(opts.headers || {}) } });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body.error || body.detail || `${r.status} ${r.statusText}`);
    return body;
  }, [base, headers]);

  const refresh = useCallback(async () => {
    setError("");
    try {
      const [s, st, lg] = await Promise.all([
        call("/api/swing-bot/settings"),
        call("/api/swing-bot/status"),
        call("/api/swing-bot/logs?limit=30"),
      ]);
      setSettings(s.settings || s);
      setStatus(st);
      setLogs(lg.logs || []);
    } catch (e) { setError(String(e.message || e)); }
  }, [call]);

  useEffect(() => { refresh(); }, [refresh]);

  const save = async (patch) => {
    setBusy(true); setError("");
    try {
      const res = await call("/api/swing-bot/settings", { method: "POST", body: JSON.stringify(patch) });
      setSettings(res.settings || res);
      await refresh();
    } catch (e) { setError(String(e.message || e)); }
    finally { setBusy(false); }
  };

  const scan = async () => {
    setBusy(true); setError("");
    try {
      const res = await call("/api/swing-bot/scan", {
        method: "POST",
        body: JSON.stringify({ limit: 10, force: true }),
      });
      setScanRows(res.candidates || []);
      const lg = await call("/api/swing-bot/logs?limit=30");
      setLogs(lg.logs || []);
    } catch (e) { setError(String(e.message || e)); }
    finally { setBusy(false); }
  };

  const runTick = async () => {
    setBusy(true); setError("");
    try {
      await call("/api/swing-bot/run-tick", {
        method: "POST",
        body: JSON.stringify({ execute: true, force_scan: true }),
      });
      setScanRows([]);
      await refresh();
    } catch (e) { setError(String(e.message || e)); }
    finally { setBusy(false); }
  };

  if (!settings) return <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-slate-300">Loading Swing Bot…</div>;
  const top = scanRows.length ? scanRows : (status?.state?.last_top_setups || []);
  const open = status?.open_trades || [];

  return (
    <div className="rounded-2xl border border-slate-700 bg-slate-950 p-4 text-slate-100 shadow-xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold">Swing Options Bot</h2>
          <p className="text-sm text-slate-400">Daily → 4h → 1h → 30m CALL/PUT setups • 20–60 DTE • v19.11.0</p>
          <p className="mt-1 text-xs text-slate-500">Scan Now works while disabled. Execution stays PAPER-only in v1.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-amber-500/15 px-3 py-1 text-xs font-semibold text-amber-300">PAPER ROLLOUT</span>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!settings.enabled} disabled={busy}
              onChange={(e) => save({ enabled: e.target.checked })} /> Enabled
          </label>
        </div>
      </div>

      {error && <div className="mt-3 rounded-lg border border-red-700 bg-red-950/40 p-2 text-sm text-red-300">{error}</div>}

      <div className="mt-4 grid gap-3 md:grid-cols-4">
        <label className="text-xs text-slate-400">Minimum score
          <input className="mt-1 w-full rounded bg-slate-900 p-2 text-slate-100" type="number" min="75" max="99"
            value={settings.min_score ?? 84} onChange={(e) => setSettings({ ...settings, min_score: Number(e.target.value) })}
            onBlur={() => save({ min_score: settings.min_score })} />
        </label>
        <label className="text-xs text-slate-400">DTE min
          <input className="mt-1 w-full rounded bg-slate-900 p-2 text-slate-100" type="number" value={settings.min_dte ?? 20}
            onChange={(e) => setSettings({ ...settings, min_dte: Number(e.target.value) })}
            onBlur={() => save({ min_dte: settings.min_dte })} />
        </label>
        <label className="text-xs text-slate-400">Preferred DTE
          <input className="mt-1 w-full rounded bg-slate-900 p-2 text-slate-100" type="number" value={settings.preferred_dte ?? 35}
            onChange={(e) => setSettings({ ...settings, preferred_dte: Number(e.target.value) })}
            onBlur={() => save({ preferred_dte: settings.preferred_dte })} />
        </label>
        <label className="text-xs text-slate-400">DTE max
          <input className="mt-1 w-full rounded bg-slate-900 p-2 text-slate-100" type="number" value={settings.max_dte ?? 60}
            onChange={(e) => setSettings({ ...settings, max_dte: Number(e.target.value) })}
            onBlur={() => save({ max_dte: settings.max_dte })} />
        </label>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button disabled={busy} onClick={scan} className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold">Scan Now</button>
        <button disabled={busy || !settings.enabled} onClick={runTick} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold disabled:opacity-40">Run 1 Paper Tick</button>
        <button disabled={busy} onClick={refresh} className="rounded-lg bg-slate-800 px-4 py-2 text-sm">Refresh</button>
      </div>

      <div className="mt-5">
        <h3 className="font-semibold">Top Swing Setups</h3>
        <div className="mt-2 grid gap-2">
          {top.length === 0 && <div className="text-sm text-slate-500">No scan results yet. NO TRADE is expected to be common.</div>}
          {top.slice(0, 10).map((x) => (
            <div key={`${x.ticker}-${x.direction}`} className="rounded-xl border border-slate-800 bg-slate-900/60 p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="font-bold">{x.ticker} · {x.direction} SWING</div>
                <div className={`font-bold ${x.qualifies ? "text-emerald-400" : "text-slate-400"}`}>{x.score}/100</div>
              </div>
              <div className="mt-1 text-xs text-slate-400">{x.reason}</div>
              <div className="mt-2 grid grid-cols-5 gap-1 text-center text-xs">
                {Object.entries(x.components || {}).map(([k, v]) => <div key={k} className="rounded bg-slate-800 p-1"><div className="text-slate-500">{k}</div><b>{Number(v).toFixed(0)}</b></div>)}
              </div>
              <div className="mt-2 text-xs text-slate-400">{x.entry_style} • invalidation ~{x.invalidation_price} • {x.expected_hold}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <div>
          <h3 className="font-semibold">Open Swing Positions</h3>
          <div className="mt-2 space-y-2 text-sm">
            {open.length === 0 ? <div className="text-slate-500">None</div> : open.map((t) => (
              <div key={t.id} className="rounded bg-slate-900 p-2">{t.underlying} {t.direction} · {t.option_symbol} · entry ${Number(t.entry_price || 0).toFixed(2)}</div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="font-semibold">Recent Swing Logs</h3>
          <div className="mt-2 max-h-64 space-y-1 overflow-auto text-xs text-slate-400">
            {logs.slice(0, 20).map((l) => <div key={l.id} className="rounded bg-slate-900 p-2">{l.ts} · {l.event} {l.symbol || ""}</div>)}
          </div>
        </div>
      </div>
    </div>
  );
}
